/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { ChangeDetectorRef, Component, Inject, NgZone, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { Select, Store } from '@ngxs/store';
import { ReCaptchaV3Service, RecaptchaComponent } from 'ng-recaptcha-2';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { RedirectHelper } from '../../../page-modules/resource/store/editor/content/helpers/redirect.helper';
import { SnackbarHelper } from '../../../shared/helpers/snackbar-helper';
import { Organization } from '../../../shared/models';
import { MaintenanceService } from '../../../shared/services/maintenance/maintenance.service';
import { AUTH_SERVICE, BasicAuthService } from '../../services/basic-auth.service';
import { UserAuthState } from '../../store/user-auth.state';

@Component({
  templateUrl: './unverified-email.component.html',
  styleUrls: ['./unverified-email.component.scss'],
})
export class UnverifiedEmailComponent implements OnInit {
  @Select(UserAuthState.organizationDetails)
  organizationData$: Observable<Organization>;

  @ViewChild('captchaElem', { static: false }) captchaElem: RecaptchaComponent;

  emailForm: FormGroup;
  isLoading: boolean;
  isRegistered: boolean;
  email: string;
  reCaptchaSiteKeyV2 = environment.reCaptchaSiteKeyV2;
  showMaintenanceOverlay: boolean;

  constructor(
    private fb: FormBuilder,
    private store: Store,
    private activatedRoute: ActivatedRoute,
    @Inject(AUTH_SERVICE) private authService: BasicAuthService,
    private router: Router,
    private snackBar: MatSnackBar,
    private ngZone: NgZone,
    private translocoService: TranslocoService,
    private cdr: ChangeDetectorRef,
    private captchaV3Service: ReCaptchaV3Service,
    private maintenanceService: MaintenanceService,
  ) {
    this.showMaintenanceOverlay = this.maintenanceService.showMaintenanceMessage();
    this.emailForm = this.fb.group({
      email: ['', Validators.compose([Validators.required, Validators.email])],
    });
  }

  ngOnInit() {
    this.email = this.activatedRoute.snapshot.queryParamMap.get('email');
    this.isRegistered = !!this.activatedRoute.snapshot.queryParamMap.get('register') && !!this.email;
    if (this.email) {
      this.emailForm.setValue({ email: this.email });
    }
  }

  onLoginClick() {
    RedirectHelper.redirectByUrl(this.ngZone, this.router, this.activatedRoute, '/signin');
  }

  resendEmailVerification() {
    if (this.emailForm.valid) {
      this.captchaElem.execute();
    }
  }

  proceedIfValid(captchaResponseV2: string | null) {
    if (!captchaResponseV2) return;
    this.proceedResend(captchaResponseV2);
  }

  private proceedResend(captchaResponseV2: string) {
    this.isLoading = true;
    const email = this.emailForm.get('email').value?.trim();
    const organizationUid = this.store.selectSnapshot(UserAuthState.organizationDetails)._id;
    this.captchaV3Service.execute('forgotPassword').subscribe((captchaResponseV3: string) => {
      this.authService
        .resendVerificationEmail(email, organizationUid, captchaResponseV3, captchaResponseV2)
        .subscribe(({ isSuccess, error }) => {
          this.isLoading = false;
          if (isSuccess) {
            RedirectHelper.redirectByUrl(this.ngZone, this.router, this.activatedRoute, '/signin');
            SnackbarHelper.showTranslatableSnackBar(this.ngZone, this.snackBar, this.translocoService, 'translations.verifyYourAccount', 0);
          } else {
            SnackbarHelper.showSnackBar(this.ngZone, this.snackBar, error);
          }
          this.captchaElem.reset();
          this.cdr.detectChanges();
        });
    });
  }
}
